import { useState, useCallback, useMemo } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

import Layout, { PrivacyModeContext, useRefresh } from "./components/Layout";
import BarcodeScanner from "./components/BarcodeScanner";
import QuickSaleModal from "./components/QuickSaleModal";
import Dashboard from "./pages/Dashboard";
import Products from "./pages/Products";
import Depozit from "./pages/Depozit";
import StorePage from "./pages/StorePage";
import Pallets from "./pages/Pallets";
import StockGeneral from "./pages/StockGeneral";
import Customers from "./pages/Customers";
import HistoryPage from "./pages/HistoryPage";
import Reports from "./pages/Reports";
import SearchPage from "./pages/SearchPage";
import Expenses from "./pages/Expenses";
import ReceptiePalet from "./pages/ReceptiePalet";
import SetariV2 from "./pages/SetariV2";
import IstoricPaleti from "./pages/IstoricPaleti";
import MagazinV2 from "./pages/MagazinV2";
import DepozitV2 from "./pages/DepozitV2";
import ScrapV2 from "./pages/ScrapV2";
import ComenziV2 from "./pages/ComenziV2";
import AcasaV2 from "./pages/AcasaV2";
import RapoarteV2 from "./pages/RapoarteV2";
import FacturiV2 from "./pages/FacturiV2";
import IstoricMiscari from "./pages/IstoricMiscari";
import BazaDateProduse from "./pages/BazaDateProduse";
import RezervariV2 from "./pages/RezervariV2";
import ComenziVirtuale from "./pages/ComenziVirtuale";
import Loguri from "./pages/Loguri";
import ClientPortal from "./pages/ClientPortal";
import LoginPage from "./pages/LoginPage";
import WebOferte from "./pages/WebOferte";
import ControlStock from "./pages/ControlStock";
import Cheltuieli from "./pages/Cheltuieli";
import ControlClienti from "./pages/ControlClienti";

import { toast } from "sonner";

// Initialize app version BEFORE any component renders
// This ensures localStorage is set before any API call
if (!localStorage.getItem('app_version')) {
    localStorage.setItem('app_version', 'v2');
}

// Protected Route component
const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-zinc-50">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-zinc-500">Se încarcă...</p>
                </div>
            </div>
        );
    }
    
    if (!user) {
        return <Navigate to="/login" replace />;
    }
    
    return children;
};

const AppContent = () => {
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [isQuickSaleOpen, setIsQuickSaleOpen] = useState(false);
    const [scannedBarcode, setScannedBarcode] = useState('');
    const [privacyMode, setPrivacyMode] = useState(() => {
        // Load from localStorage on init
        const saved = localStorage.getItem('safeMode');
        return saved === 'true';
    });
    const navigate = useNavigate();

    const togglePrivacyMode = () => {
        setPrivacyMode(prev => {
            const newValue = !prev;
            localStorage.setItem('safeMode', newValue.toString());
            toast.info(newValue ? 'Safe Mode activ' : 'Safe Mode dezactivat');
            return newValue;
        });
    };

    // New flow: Scanner -> Quick Sale Modal
    const handleScan = useCallback((barcode) => {
        setScannedBarcode(barcode);
        setIsScannerOpen(false);
        setIsQuickSaleOpen(true);
    }, []);

    // After quick sale completes, trigger refresh in Layout
    const handleQuickSaleComplete = useCallback(() => {
        // The Layout component has a refresh context that will update sidebar stats
        // We can dispatch a custom event to trigger refresh
        window.dispatchEvent(new Event('quicksale-complete'));
    }, []);

    // Memoize context value to prevent unnecessary re-renders
    const privacyContextValue = useMemo(() => ({ privacyMode, togglePrivacyMode }), [privacyMode, togglePrivacyMode]);

    return (
        <PrivacyModeContext.Provider value={privacyContextValue}>
            <Layout 
                onScanClick={() => setIsScannerOpen(true)}
                privacyMode={privacyMode}
                togglePrivacyMode={togglePrivacyMode}
            >
                <Routes>
                    <Route path="/" element={<AcasaV2 />} />
                    <Route path="/control-clienti" element={<ControlClienti />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/products" element={<Products />} />
                    <Route path="/depozit" element={<DepozitV2 />} />
                    <Route path="/store" element={<StorePage />} />
                    <Route path="/pallets" element={<Pallets safeMode={privacyMode} />} />
                    <Route path="/stock-general" element={<StockGeneral />} />
                    <Route path="/customers" element={<Customers privacyMode={privacyMode} />} />
                    <Route path="/history" element={<HistoryPage privacyMode={privacyMode} />} />
                    <Route path="/reports" element={<Reports safeMode={privacyMode} />} />
                    <Route path="/expenses" element={<Expenses />} />
                    <Route path="/search" element={<SearchPage />} />
                    <Route path="/receptie" element={<ReceptiePalet />} />
                    <Route path="/setari" element={<SetariV2 />} />
                    <Route path="/istoric-paleti" element={<IstoricPaleti />} />
                    <Route path="/magazin-v2" element={<MagazinV2 />} />
                    <Route path="/depozit-v2" element={<DepozitV2 />} />
                    <Route path="/scrap-v2" element={<ScrapV2 />} />
                    <Route path="/comenzi-v2" element={<ComenziV2 />} />
                    <Route path="/acasa" element={<AcasaV2 />} />
                    <Route path="/rapoarte-v2" element={<RapoarteV2 />} />
                    <Route path="/facturi" element={<FacturiV2 />} />
                    <Route path="/baza-date" element={<BazaDateProduse />} />
                    <Route path="/rezervari" element={<RezervariV2 />} />
                    <Route path="/comenzi-virtuale" element={<ComenziVirtuale />} />
                    <Route path="/loguri" element={<Loguri />} />
                    <Route path="/istoric-miscari" element={<IstoricMiscari />} />
                    <Route path="/web-oferte" element={<WebOferte />} />
                    <Route path="/control-stock" element={<ControlStock />} />
                    <Route path="/cheltuieli" element={<Cheltuieli />} />
                </Routes>
            </Layout>
            
            <BarcodeScanner
                isOpen={isScannerOpen}
                onClose={() => setIsScannerOpen(false)}
                onScan={handleScan}
            />
            
            <QuickSaleModal
                isOpen={isQuickSaleOpen}
                onClose={() => setIsQuickSaleOpen(false)}
                scannedBarcode={scannedBarcode}
                privacyMode={privacyMode}
                onComplete={handleQuickSaleComplete}
            />
            
            <Toaster position="top-center" richColors />
        </PrivacyModeContext.Provider>
    );
};

// Component to handle domain-based redirect
function DomainRedirect() {
    const hostname = window.location.hostname;
    
    // If accessing from toth-box.ro, redirect to portal
    if (hostname === 'toth-box.ro' || hostname === 'www.toth-box.ro') {
        window.location.replace('/portal');
        return null;
    }
    
    // Otherwise show admin app
    return <AppContent />;
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <Routes>
                        {/* Public routes - no auth required */}
                        <Route path="/portal" element={<ClientPortal />} />
                        <Route path="/login" element={<LoginPage />} />
                        
                        {/* Root path - redirect based on domain */}
                        <Route path="/*" element={<DomainRedirect />} />
                        
                        {/* Admin App - protected with auth */}
                        <Route path="/*" element={
                            <ProtectedRoute>
                                <AppContent />
                            </ProtectedRoute>
                        } />
                    </Routes>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
